import ENDPOINTS from './endPoints';
import apiService from './service/apiService';

export interface EditContentPayload {
  content_id: string | number;
  post_copy: string;
  visual_brief?: {
    image_prompt?: string;
    image_style?: string;
    aspect_ratio?: string;
    video_idea?: string;
  };
}

export const editContent = async (
  contentId: string,
  restaurantId: string,
  payload: {
    post_copy: string;
    subject_line?: string;
    visual_brief?: any;
  }
): Promise<any> => {
  const formData = new FormData();
  formData.append('content_id', contentId);
  formData.append('restaurant_id', restaurantId);
  formData.append('image', ''); // Empty image value
  
  // Build content object - include subject_line if present (for Email)
  const contentObj: any = {
    post_copy: payload.post_copy,
    visual_brief: payload.visual_brief
  };
  
  if (payload.subject_line) {
    contentObj.subject_line = payload.subject_line;
  }
  
  formData.append('content', JSON.stringify(contentObj));

  // URL uses restaurant_id, content_id is only in FormData
  return await apiService.put({
    endpoint: `${ENDPOINTS.editFlyer}/${restaurantId}`,
    body: formData
  });
};

export const createDirectCampaign = async (
  restaurantId: string,
  payload: {
    campaign_title: string;
    campaign_date: string;
    campaign_description: string;
    campaign_type: string;
    flyer?: File | null;
  }
): Promise<any> => {
  const formData = new FormData();
  formData.append('campaign_title', payload.campaign_title);
  formData.append('campaign_date', payload.campaign_date);
  formData.append('campaign_description', payload.campaign_description);
  formData.append('campaign_type', payload.campaign_type);
  
  // Add flyer if provided, otherwise send empty
  if (payload.flyer) {
    formData.append('flyer', payload.flyer);
  } else {
    formData.append('flyer', '');
  }

  return await apiService.post({
    endpoint: `${ENDPOINTS.directCampaign}/${restaurantId}`,
    body: formData
  });
};

export const regenerateFlyer = async (
  contentId: string,
  restaurantId: string,
  flyerContent: {
    Header: string;
    Description: string;
    'Offer Details': string;
    Footer: string;
    hero_image_prompt: string;
  }
): Promise<any> => {
  const formData = new FormData();
  formData.append('content_id', contentId);
  // Don't send restaurant_id in payload - it's already in the URL
  formData.append('image', ''); // Empty image value
  
  // Build content object with flyer fields
  const contentObj = {
    Header: flyerContent.Header,
    hero_image_prompt: flyerContent.hero_image_prompt,
    Description: flyerContent.Description,
    'Offer Details': flyerContent['Offer Details'],
    Footer: flyerContent.Footer
  };
  
  formData.append('content', JSON.stringify(contentObj));

  // Restaurant ID is only in URL, not in FormData payload
  return await apiService.put({
    endpoint: `${ENDPOINTS.editFlyer}/${restaurantId}`,
    body: formData
  });
};

export const updateMediaCaption = async (
  campaignId: string,
  restaurantId: string,
  platform: string,
  postCopy: string
): Promise<any> => {
  // First, get the full campaign data
  const campaignResponse = await apiService.get({
    endpoint: ENDPOINTS.getCampaign(campaignId),
    headers: {
      'restaurant-id': restaurantId
    }
  });

  // Access the nested data correctly
  const campaignData = campaignResponse.data.data || campaignResponse.data;

  // Update the specific channel's caption in the channels array
  const updatedChannels = (campaignData.channels || []).map((channel: any) => {
    if (channel.name.toLowerCase() === platform.toLowerCase()) {
      return {
        ...channel,
        caption: postCopy
      };
    }
    return channel;
  });

  // Clean up the payload - remove response wrapper fields
  const { id, accountId, restaurantId: resId, promptId, flyerGenerationCount, publishData, createdAt, updatedAt, ...cleanData } = campaignData;

  // Send back only the necessary campaign fields with updated channels
  return await apiService.put({
    endpoint: ENDPOINTS.updateCampaignCaption(campaignId),
    body: {
      title: cleanData.title,
      type: cleanData.type,
      eventId: cleanData.campaignData?.eventId || cleanData.eventId,
      event: cleanData.event || cleanData.campaignData?.event,
      offerDetails: cleanData.campaignData?.offerDetails || cleanData.offerDetails,
      instructions: cleanData.campaignData?.instructions || cleanData.instructions || '',
      description: cleanData.campaignData?.description || cleanData.description,
      tagLine: cleanData.campaignData?.tagLine || cleanData.tagLine || '',
      contactInfo: cleanData.campaignData?.contactInfo || cleanData.contactInfo || '',
      themeColour: cleanData.campaignData?.themeColour || cleanData.themeColour,
      validFrom: cleanData.campaignData?.validFrom || cleanData.validFrom,
      validTo: cleanData.campaignData?.validTo || cleanData.validTo,
      menuItems: cleanData.campaignData?.menuItems || cleanData.menuItems || [],
      refImage: cleanData.campaignData?.refImage || cleanData.refImage || [],
      combo: cleanData.campaignData?.combo || cleanData.combo || [],
      channels: updatedChannels,
      flyer: cleanData.flyer || {}
    },
    headers: {
      'restaurant-id': restaurantId
    }
  });
};

/**
 * Get flyer details by content ID
 */
export const getFlyer = async (
  restaurantId: string,
  contentId: string
): Promise<any> => {
  const response = await apiService.get({
    endpoint: `${ENDPOINTS.getFlyer}/${restaurantId}?content_id=${contentId}`
  });
  return response.data;
};

/**
 * Get campaign details by campaign ID
 */
export const getCampaign = async (
  campaignId: string,
  restaurantId: string
): Promise<any> => {
  const response = await apiService.get({
    endpoint: ENDPOINTS.getCampaign(campaignId),
    headers: {
      'restaurant-id': restaurantId
    }
  });
  return response.data;
};

/**
 * Update campaign by campaign ID
 */
export const updateCampaign = async (
  campaignId: string,
  restaurantId: string,
  payload: any
): Promise<any> => {
  const response = await apiService.put({
    endpoint: ENDPOINTS.updateCampaign(campaignId),
    body: payload,
    headers: {
      'restaurant-id': restaurantId
    }
  });
  return response.data;
};

export default {
  editContent,
  regenerateFlyer,
  createDirectCampaign,
  updateMediaCaption,
  getFlyer,
  getCampaign,
  updateCampaign
};
